import React, { useState } from 'react';
import { rateClientsAPI } from '../services/rateClientsAPI';
import ConfirmModal from './ConfirmModal.jsx';
import '../styles/ClientModal.css';

const ClientModal = ({ isOpen, onClose, onClientCreated }) => {
  const [activeTab, setActiveTab] = useState('add'); // 'add', 'edit', or 'upload'
  
  // Helper functions for percentage conversion
  // Values < 1 are stored as decimals (0.03 = 3%), values >= 1 are percentages (8 = 8%)
  const toPercentValue = (val) => {
    const num = parseFloat(val);
    if (!Number.isFinite(num)) return null;
    return num < 1 ? num * 100 : num;
  };

  const toDecimalValue = (val) => {
    const num = parseFloat(val);
    if (!Number.isFinite(num) || num === 0) return null;
    // Convert percentage to decimal for storage (3 -> 0.03, 0.03 -> 0.03)
    return num >= 1 ? num / 100 : num;
  };

  const [formData, setFormData] = useState({
    title: '',
    msp: '',
    tool: '',
    mspFee: '',
    otherDiscounts: '',
    totalDiscounts: '',
    markupW2: '',
    markupC2C: '',
    c2cOverride: false
  });
  const [csvFile, setCSVFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await rateClientsAPI.getClients();
      setClients(response.data || []);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccessMessage('');
    
    // Clear form when switching to Add tab
    if (tab === 'add') {
      setFormData({
        title: '',
        msp: '',
        tool: '',
        mspFee: '',
        otherDiscounts: '',
        totalDiscounts: '',
        markupW2: '',
        markupC2C: '',
        c2cFriendly: '',
        conversionMaxLength: '',
        startingFee: '',
        otRate: ''
      });
    }
    
    if (tab === 'edit' && clients.length === 0) {
      loadClients();
    }
  };

  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);
    
    if (clientId) {
      const client = clients.find(c => c.ClientId === parseInt(clientId));
      if (client) {
        setFormData({
          title: client.Title || '',
          msp: client.MSP || '',
          tool: client.Tool || '',
          mspFee: toPercentValue(client.MSPFee) ?? '',
          otherDiscounts: client.OtherDiscounts || '',
          totalDiscounts: toPercentValue(client.TotalDiscounts) ?? '',
          markupW2: toPercentValue(client.MarkupW2) ?? '',
          markupC2C: toPercentValue(client.MarkupC2C) ?? '',
          c2cOverride: client.C2COverride || false
        });
      }
    } else {
      setFormData({
        title: '',
        msp: '',
        tool: '',
        mspFee: '',
        otherDiscounts: '',
        totalDiscounts: '',
        markupW2: '',
        markupC2C: '',
        c2cOverride: false
      });
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Client title is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Convert percentage values to decimals for storage
      const clientData = {
        ...formData,
        mspFee: toDecimalValue(formData.mspFee),
        totalDiscounts: toDecimalValue(formData.totalDiscounts),
        markupW2: toDecimalValue(formData.markupW2),
        markupC2C: toDecimalValue(formData.markupC2C)
      };
      
      await rateClientsAPI.createClient(clientData);
      setSuccessMessage('Client added successfully!');
      setFormData({
        title: '',
        msp: '',
        tool: '',
        mspFee: '',
        otherDiscounts: '',
        totalDiscounts: '',
        markupW2: '',
        markupC2C: '',
        c2cOverride: false
      });
      setTimeout(() => {
        setSuccessMessage('');
        onClientCreated();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      setError('Please select a client to edit');
      return;
    }
    if (!formData.title.trim()) {
      setError('Client title is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      // Convert percentage values to decimals for storage
      const clientData = {
        ...formData,
        mspFee: toDecimalValue(formData.mspFee),
        totalDiscounts: toDecimalValue(formData.totalDiscounts),
        markupW2: toDecimalValue(formData.markupW2),
        markupC2C: toDecimalValue(formData.markupC2C)
      };
      
      await rateClientsAPI.updateClient(selectedClientId, clientData);
      
      // Reload the clients list in the modal
      await loadClients();
      
      // Reload the specific client being edited to show fresh data
      const updatedClientResponse = await rateClientsAPI.getClient(selectedClientId);
      const updatedClient = updatedClientResponse.data;
      
      // Update the form data with the reloaded client data (convert back to percentages for display)
      setFormData({
        title: updatedClient.Title || '',
        msp: updatedClient.MSP || '',
        tool: updatedClient.Tool || '',
        mspFee: toPercentValue(updatedClient.MSPFee) ?? '',
        otherDiscounts: updatedClient.OtherDiscounts || '',
        totalDiscounts: toPercentValue(updatedClient.TotalDiscounts) ?? '',
        markupW2: toPercentValue(updatedClient.MarkupW2) ?? '',
        markupC2C: toPercentValue(updatedClient.MarkupC2C) ?? '',
        c2cOverride: updatedClient.C2COverride || false
      });
      
      setSuccessMessage('Client updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClientId) {
      setError('Please select a client to delete');
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteClient = async () => {
    if (!selectedClientId) return;

    try {
      setLoading(true);
      setError('');
      await rateClientsAPI.deleteClient(selectedClientId);
      setSuccessMessage('Client deleted successfully!');
      setSelectedClientId('');
      setFormData({
        title: '',
        msp: '',
        tool: '',
        mspFee: '',
        otherDiscounts: '',
        totalDiscounts: '',
        markupW2: '',
        markupC2C: '',
        c2cFriendly: '',
        conversionMaxLength: '',
        startingFee: '',
        otRate: ''
      });
      await loadClients(); // Reload clients to update the dropdown
      setTimeout(() => {
        setSuccessMessage('');
        onClientCreated();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete client');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUploadCSV = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await rateClientsAPI.uploadClientsFromCSV(csvFile);
      const { imported, total, errors } = response.data;
      
      if (errors.length > 0) {
        setError(`Imported ${imported}/${total} clients. Errors: ${errors.join('; ')}`);
      } else {
        setSuccessMessage(`Successfully imported ${imported} clients!`);
      }
      
      setCSVFile(null);
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      setTimeout(() => {
        setSuccessMessage('');
        setError('');
        onClientCreated();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload CSV');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <ConfirmModal
          show={showDeleteConfirm}
          title="Delete Client"
          message={`Are you sure you want to delete "${clients.find(c => c.ClientId === parseInt(selectedClientId))?.Title || 'this client'}"? This action cannot be undone.`}
          onConfirm={confirmDeleteClient}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText="Delete"
          confirmStyle="danger"
        />

        <div className="modal-header">
          <h2>Manage Clients</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => handleTabChange('add')}
          >
            Add Client
          </button>
          <button
            className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => handleTabChange('edit')}
          >
            Edit Client
          </button>
          <button
            className={`tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => handleTabChange('upload')}
          >
            Upload CSV
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}

          {activeTab === 'add' && (
            <form onSubmit={handleAddClient}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Client Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="msp">MSP</label>
                  <input
                    type="text"
                    id="msp"
                    name="msp"
                    value={formData.msp}
                    onChange={handleInputChange}
                    placeholder="MSP name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="tool">Tool</label>
                  <input
                    type="text"
                    id="tool"
                    name="tool"
                    value={formData.tool}
                    onChange={handleInputChange}
                    placeholder="Tool/Service"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mspFee">MSP Fee</label>
                  <input
                    type="number"
                    id="mspFee"
                    name="mspFee"
                    value={formData.mspFee}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="totalDiscounts">Total Discounts</label>
                  <input
                    type="number"
                    id="totalDiscounts"
                    name="totalDiscounts"
                    value={formData.totalDiscounts}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="otherDiscounts">Other Discounts</label>
                  <input
                    type="text"
                    id="otherDiscounts"
                    name="otherDiscounts"
                    value={formData.otherDiscounts}
                    onChange={handleInputChange}
                    placeholder="Discount details"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="markupW2">Markup W2 %</label>
                  <input
                    type="number"
                    id="markupW2"
                    name="markupW2"
                    value={formData.markupW2}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="markupC2C">Markup C2C %</label>
                  <input
                    type="number"
                    id="markupC2C"
                    name="markupC2C"
                    value={formData.markupC2C}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? 'Adding...' : 'Add Client'}
              </button>
            </form>
          )}

          {activeTab === 'edit' && (
            <form onSubmit={handleEditClient}>
              <div className="form-group">
                <label htmlFor="clientSelect">Select Client *</label>
                <select
                  id="clientSelect"
                  value={selectedClientId}
                  onChange={handleClientSelect}
                  className="client-select"
                  required
                >
                  <option value="">-- Select a client to edit --</option>
                  {clients.map(client => (
                    <option key={client.ClientId} value={client.ClientId}>
                      {client.Title}
                    </option>
                  ))}
                </select>
              </div>

              {selectedClientId && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-title">Client Title *</label>
                      <input
                        type="text"
                        id="edit-title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter client name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-msp">MSP</label>
                      <input
                        type="text"
                        id="edit-msp"
                        name="msp"
                        value={formData.msp}
                        onChange={handleInputChange}
                        placeholder="MSP name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-tool">Tool</label>
                      <input
                        type="text"
                        id="edit-tool"
                        name="tool"
                        value={formData.tool}
                        onChange={handleInputChange}
                        placeholder="Tool/Service"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-mspFee">MSP Fee</label>
                      <input
                        type="number"
                        id="edit-mspFee"
                        name="mspFee"
                        value={formData.mspFee}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-totalDiscounts">Total Discounts</label>
                      <input
                        type="number"
                        id="edit-totalDiscounts"
                        name="totalDiscounts"
                        value={formData.totalDiscounts}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-otherDiscounts">Other Discounts</label>
                      <input
                        type="text"
                        id="edit-otherDiscounts"
                        name="otherDiscounts"
                        value={formData.otherDiscounts}
                        onChange={handleInputChange}
                        placeholder="Discount details"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-markupW2">Markup W2 %</label>
                      <input
                        type="number"
                        id="edit-markupW2"
                        name="markupW2"
                        value={formData.markupW2}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-markupC2C">Markup C2C %</label>
                      <input
                        type="number"
                        id="edit-markupC2C"
                        name="markupC2C"
                        value={formData.markupC2C}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group checkbox-group">
                      <label htmlFor="edit-c2cOverride">
                        <input
                          type="checkbox"
                          id="edit-c2cOverride"
                          name="c2cOverride"
                          checked={formData.c2cOverride}
                          onChange={handleInputChange}
                        />
                        Allow Loaded Cost for C2C
                      </label>
                    </div>
                  </div>

                  <div className="button-group">
                    <button type="submit" disabled={loading} className="submit-btn">
                      {loading ? 'Updating...' : 'Update Client'}
                    </button>
                    <button 
                      type="button" 
                      onClick={handleDeleteClient} 
                      disabled={loading} 
                      className="delete-btn"
                    >
                      {loading ? 'Deleting...' : 'Delete Client'}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}

          {activeTab === 'upload' && (
            <form onSubmit={handleUploadCSV}>
              <div className="form-group">
                <label htmlFor="csvFile">CSV File</label>
                <p className="csv-hint">
                  Upload a CSV file with headers. Example format:<br/>
                  Title,MSP,Tool,MarkupW2,MarkupC2C,StartingFee<br/>
                  Acme Corp,MSP1,Tool1,30,45,5000<br/>
                  TechCorp,MSP2,Tool2,25,40,4000
                </p>
                <input
                  type="file"
                  id="csvFile"
                  accept=".csv"
                  onChange={(e) => setCSVFile(e.target.files[0])}
                  className="file-input"
                />
                {csvFile && (
                  <p className="file-selected">Selected: {csvFile.name}</p>
                )}
              </div>

              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? 'Uploading...' : 'Import Clients'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientModal;
